import type {
  CalendarAuthAction,
  GoogleAuthToken,
  MicrosoftAuthToken,
} from 'src/action-creators/calendar-auth';
import {
  SET_GOOGLE_AUTH_TOKEN,
  SET_MICROSOFT_AUTH_TOKEN,
  CLEAR_CALENDAR_AUTH,
} from 'src/action-creators/calendar-auth';


export type CalendarAuthState = {
  googleAuthToken: GoogleAuthToken | null | undefined;
  microsoftAuthToken: MicrosoftAuthToken | null | undefined;
  isGoogleAuthenticated: boolean;
  isMicrosoftAuthenticated: boolean;
};

export const initialState: CalendarAuthState = {
  googleAuthToken: null,
  microsoftAuthToken: null,
  isGoogleAuthenticated: false,
  isMicrosoftAuthenticated: false,
};

/**
 * Check if Google token is valid
 */
const isGoogleTokenValid = (token?: GoogleAuthToken | null): boolean => {
  if (!token) {
    return false;
  }

  try {
    const now = new Date();
    const tokenIssuedAt = new Date(token.issued_at);
    const tokenLifeSeconds = (now.getTime() - tokenIssuedAt.getTime()) / 1000;

    return tokenLifeSeconds < token.expires_in;
  } catch (error: any) {
    console.error('Error checking Google token validity:', error);
    return false;
  }
};

/**
 * Check if Microsoft token is valid
 */
const isMicrosoftTokenValid = (token?: MicrosoftAuthToken | null): boolean => {
  if (!token) {
    return false;
  }

  try {
    const now = new Date();
    return token.expiresOn > now;
  } catch (error: any) {
    console.error('Error checking Microsoft token validity:', error);
    return false;
  }
};

/**
 * Reducer for calendar authentication
 */
const calendarAuthReducer = (
  state: CalendarAuthState = initialState,
  action: CalendarAuthAction,
): CalendarAuthState => {
  switch (action.type) {
    case SET_GOOGLE_AUTH_TOKEN:
      return {
        ...state,
        googleAuthToken: action.payload,
        isGoogleAuthenticated: isGoogleTokenValid(action.payload),
      };

    case SET_MICROSOFT_AUTH_TOKEN:
      return {
        ...state,
        microsoftAuthToken: action.payload,
        isMicrosoftAuthenticated: isMicrosoftTokenValid(action.payload),
      };

    case CLEAR_CALENDAR_AUTH:
      return initialState;

    default:
      return state;
  }
};

export default calendarAuthReducer;
